import * as React from "react";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import dashboardImg from "../dashboard.png";

interface HeroSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: { regular: string; gradient: string };
  description?: string;
  ctaText?: string;
  ctaHref?: string;
  bottomImage?: { light: string; dark: string };
  gridOptions?: {
    angle?: number;
    cellSize?: number;
    opacity?: number;
    lightLineColor?: string;
    darkLineColor?: string;
  };
}

const RetroGrid = ({
  angle = 65,
  cellSize = 60,
  opacity = 0.5,
  lightLineColor = "gray",
  darkLineColor = "gray",
}) => {
  const gridStyles = {
    "--grid-angle": `${angle}deg`,
    "--cell-size": `${cellSize}px`,
    "--opacity": opacity as unknown as string,
    "--light-line": lightLineColor,
    "--dark-line": darkLineColor,
  } as React.CSSProperties;

  return (
    <div
      className={cn(
        "pointer-events-none absolute size-full overflow-hidden [perspective:200px]",
        `opacity-[var(--opacity)]`,
      )}
      style={gridStyles}
    >
      <div className="absolute inset-0 [transform:rotateX(var(--grid-angle))]">
        <div className="animate-grid [background-image:linear-gradient(to_right,var(--light-line)_1px,transparent_0),linear-gradient(to_bottom,var(--light-line)_1px,transparent_0)] [background-repeat:repeat] [background-size:var(--cell-size)_var(--cell-size)] [height:300vh] [inset:0%_0px] [margin-left:-200%] [transform-origin:100%_0_0] [width:600vw] dark:[background-image:linear-gradient(to_right,var(--dark-line)_1px,transparent_0),linear-gradient(to_bottom,var(--dark-line)_1px,transparent_0)]" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent to-90% dark:from-black" />
    </div>
  );
};

const HeroSection = React.forwardRef<HTMLDivElement, HeroSectionProps>(
  (
    {
      className,
      title = "Build products for everyone",
      subtitle = { regular: "Designing your projects faster with ", gradient: "the largest figma UI kit." },
      description = "Sed ut perspiciatis unde omnis iste natus voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae.",
      ctaText = "Browse courses",
      ctaHref = "#",
      bottomImage = { light: dashboardImg, dark: dashboardImg },
      gridOptions,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={cn("relative", className)} ref={ref} {...props}>
        <section className="relative max-w-full mx-auto z-1 bg-transparent">
          <RetroGrid {...gridOptions} />
          <div className="max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl leading-0 lg:leading-5 mx-auto text-center">
              <h1 className="text-sm text-gray-300 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/5 via-gray-400/10 to-transparent border-[2px] border-white/10 rounded-3xl w-fit">
                {title}
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)] bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                {subtitle.regular}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">{subtitle.gradient}</span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-400">{description}</p>
              <div className="items-center justify-center gap-x-3 space-y-3 sm:flex sm:space-y-0">
                <span className="relative inline-block overflow-hidden rounded-full p-[1.5px]">
                  <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]" />
                  <div className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-gray-900 text-xs font-medium backdrop-blur-3xl">
                    <a href={ctaHref} className="inline-flex rounded-full text-center group items-center w-full justify-center bg-gradient-to-tr from-zinc-300/20 via-purple-400/30 to-transparent dark:from-zinc-300/5 dark:via-purple-400/20 text-gray-900 dark:text-white border-input border-[1px] hover:bg-gradient-to-tr hover:from-zinc-300/30 hover:via-purple-400/40 hover:to-transparent dark:hover:from-zinc-300/10 dark:hover:via-purple-400/30 transition-all sm:w-auto py-4 px-10">
                      {ctaText}
                    </a>
                  </div>
                </span>
              </div>
            </div>
            {bottomImage && (
              <motion.div
                className="mt-24 md:mt-32 mx-4 md:mx-10 relative z-10"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                <div className="absolute -inset-x-10 -top-10 h-[220px] bg-gradient-to-r from-blue-500/15 via-cyan-500/10 to-purple-500/15 blur-3xl -z-10 rounded-[40px]" />
                <div className="mx-auto max-w-6xl rounded-[28px] bg-[#0b0f1a]/80 ring-1 ring-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl p-3">
                  <div className="h-6 flex items-center justify-between px-4 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="size-2.5 rounded-full bg-red-400/70" />
                      <span className="size-2.5 rounded-full bg-yellow-400/70" />
                      <span className="size-2.5 rounded-full bg-green-400/70" />
                    </div>
                    <span className="size-2 rounded-full bg-white/20" />
                  </div>
                  <div className="relative rounded-[20px] overflow-hidden bg-black ring-1 ring-white/10">
                    <img
                      src={bottomImage.light}
                      className="w-full dark:hidden [mask-image:linear-gradient(to_bottom,black,black,transparent_97%)]"
                      alt="Dashboard preview"
                    />
                    <img
                      src={bottomImage.dark}
                      className="hidden dark:block w-full [mask-image:linear-gradient(to_bottom,black,black,transparent_97%)]"
                      alt="Dashboard preview"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-[#0b0f1a]/0 to-[#0b0f1a]/40" />
                  </div>
                </div>
                <div className="mx-auto mt-6 h-6 w-3/5 bg-gradient-to-r from-white/10 via-white/5 to-transparent rounded-full blur-md" />
              </motion.div>
            )}
          </div>
        </section>
      </div>
    );
  },
);
HeroSection.displayName = "HeroSection";

export { HeroSection };
